import type { BillingView } from "@mailhand/core/api";
import { METERED_COUNTERS, PLANS, type Plan, formatCount, formatMicrosUsd, formatUsd, overageMicros, periodStart } from "@mailhand/core/billing";
import { PRODUCT_NAME } from "@mailhand/core/brand";

const monthFormat = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const dayFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** The banner shows from this share of an allowance. */
const NOTICE_SHARE = 0.8;

/** "October 2026" */
export function periodLabel(view: BillingView): string {
  return monthFormat.format(periodStart(view.period));
}

/** "Nov 1": the first day of the next period. */
export function renewalLabel(view: BillingView): string {
  return dayFormat.format(new Date(view.periodEndsAt));
}

/** The largest share of an allowance used this period, from 0. */
export function usedShare(view: BillingView): number {
  return Math.max(...METERED_COUNTERS.map((counter) => view.usage[counter] / view.allowance[counter]));
}

export interface Banner {
  text: string;
  cta: string;
}

/** The banner every screen shows: at a monthly hold, or from 80% of an allowance. */
export function billingBanner(view: BillingView): Banner | null {
  if (view.hold === "free_limit") {
    return { text: `You used all ${formatCount(view.allowance.messages)} free emails this month. ${PRODUCT_NAME} holds new email until ${renewalLabel(view)}.`, cta: "Upgrade →" };
  }
  if (view.hold === "bill_limit" && view.billLimitUsd !== null) {
    return { text: `You reached your bill limit of ${formatUsd(view.billLimitUsd)}. ${PRODUCT_NAME} holds new email.`, cta: "Raise the limit →" };
  }
  const share = usedShare(view);
  if (share < NOTICE_SHARE) return null;
  return { text: `You used ${String(Math.floor(share * 100))}% of this month's plan.`, cta: "Billing →" };
}

/** The lines of a plan card. */
export function planLines(plan: Plan): string[] {
  if (plan.overageUnits === null) return [`${formatCount(plan.included.messages)} emails a month`, "Then email is held until the 1st"];
  return [
    `${formatCount(plan.included.messages)} emails a month`,
    `${formatCount(plan.included.writerCalls)} written replies`,
    `Then ${formatMicrosUsd(overageMicros(plan, "messages"), "unit")} an email, ${formatMicrosUsd(overageMicros(plan, "writerCalls"), "unit")} a written reply`,
  ];
}

export function planPrice(plan: Plan): string {
  return plan.priceUsd === 0 ? "Free" : `${formatUsd(plan.priceUsd)} / month`;
}

/** A warning about the subscription, when the member must act or the plan is about to change. */
export function subscriptionNote(view: BillingView): string | null {
  if (view.subscriptionStatus === "past_due") return "The last payment failed. Update the card in Manage billing.";
  if (view.state === "paymentProblem") {
    const plan = view.subscribedPlan === null ? "paid" : PLANS[view.subscribedPlan].name;
    return `Payment needed. Your ${plan} plan is not paid, so Free limits apply. Pay in Manage billing, or start a new plan.`;
  }
  if (view.cancelAtPeriodEnd) return `Your plan ends on ${renewalLabel(view)}. Then you are on ${PLANS.free.name}.`;
  return null;
}
